import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, ListGroup } from 'react-bootstrap';
import { apiHandler } from '../../api/apihandler';
import { FOLLOWER_LIST } from '../../api/constants';
import './tagFriend.css';

const TagFriendSelector = ({ show, onHide, onSelectFriends, selectedFriends = [] }) => {
   const [followerList, setFollowerList] = useState([]);
   const [filteredFollowers, setFilteredFollowers] = useState([]);
   const [searchQuery, setSearchQuery] = useState('');
   const [loading, setLoading] = useState(false);
   const [tempSelectedFriends, setTempSelectedFriends] = useState(selectedFriends);

   useEffect(() => {
      if (show) {
         fetchFollowers();
         setTempSelectedFriends(selectedFriends);
      }
   }, [show, selectedFriends]);

   useEffect(() => {
      // Filter followers based on search query
      if (searchQuery.trim() === '') {
         setFilteredFollowers(followerList);
      } else {
         const filtered = followerList.filter(follower => {
            const fullName = `${follower?.follower?.first_name || ''} ${follower?.follower?.last_name || ''}`.toLowerCase();
            return fullName.includes(searchQuery.toLowerCase());
         });
         setFilteredFollowers(filtered);
      }
   }, [searchQuery, followerList]);

   const fetchFollowers = async () => {
      setLoading(true);
      try {
         const res = await apiHandler(FOLLOWER_LIST, "POST", {
            page: 1,
            limit: 1000 // Get all followers for tagging
         });
         if (res.data?.response?.status === 200) {
            const followers = res?.data?.data?.followers?.data || [];
            setFollowerList(followers);
            setFilteredFollowers(followers);
         }
      } catch (error) {
         console.error('Error fetching followers:', error);
      } finally {
         setLoading(false);
      }
   };

   const handleToggleFriend = (follower) => {
      const userId = follower?.follower?.id;
      const isSelected = tempSelectedFriends.some(f => f.id === userId);
      
      if (isSelected) {
         setTempSelectedFriends(tempSelectedFriends.filter(f => f.id !== userId));
      } else {
         const friendData = {
            id: userId,
            first_name: follower?.follower?.first_name,
            last_name: follower?.follower?.last_name,
            photo: follower?.follower?.user_setting?.photo,
            slug: follower?.follower?.slug
         };
         setTempSelectedFriends([...tempSelectedFriends, friendData]);
      }
   };

   const handleDone = () => {
      onSelectFriends(tempSelectedFriends);
      onHide();
   };

   const handleCancel = () => {
      setTempSelectedFriends(selectedFriends);
      setSearchQuery('');
      onHide();
   };

   const isSelected = (followerId) => {
      return tempSelectedFriends.some(f => f.id === followerId);
   };

   return (
      <Modal show={show} onHide={handleCancel} size="md" centered>
         <Modal.Header className="d-flex justify-content-between">
            <h5 className="modal-title">Tag Followers</h5>
            <button type="button" className="btn btn-secondary lh-1" onClick={handleCancel}>
               <span className="material-symbols-outlined">close</span>
            </button>
         </Modal.Header>
         <Modal.Body>
            {/* Search Input */}
            <Form.Group className="mb-3">
               <Form.Control
                  type="text"
                  placeholder="Search followers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
               />
            </Form.Group>

            {/* Selected Friends Display */}
            {tempSelectedFriends.length > 0 && (
               <div className="mb-3">
                  <small className="text-muted">Selected ({tempSelectedFriends.length})</small>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                     {tempSelectedFriends.map(friend => (
                        <div key={friend.id} className="tag-friend-chip">
                           <img 
                              src={friend.photo || "https://uzone360live.s3.amazonaws.com/noimage.png"} 
                              alt={friend.first_name}
                              className="tag-friend-chip-img"
                           />
                           <span className="tag-friend-chip-name">
                              {friend.first_name} {friend.last_name}
                           </span>
                           <span 
                              className="tag-friend-chip-remove"
                              onClick={() => handleToggleFriend({ follower: { 
                                 id: friend.id,
                                 first_name: friend.first_name,
                                 last_name: friend.last_name,
                                 user_setting: { photo: friend.photo },
                                 slug: friend.slug
                              }})}
                           >
                              ×
                           </span>
                        </div>
                     ))}
                  </div>
               </div>
            )}

            {/* Followers List */}
            {loading ? (
               <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                     <span className="visually-hidden">Loading...</span>
                  </div>
               </div>
            ) : (
               <div className="tag-friend-list">
                  {filteredFollowers.length === 0 ? (
                     <p className="text-center text-muted py-4">
                        {searchQuery ? 'No followers found' : 'No followers available'}
                     </p>
                  ) : (
                     <ListGroup>
                        {filteredFollowers.map((follower) => {
                           const followerId = follower?.follower?.id;
                           const selected = isSelected(followerId);
                           
                           return (
                              <ListGroup.Item
                                 key={followerId}
                                 className={`tag-friend-item ${selected ? 'selected' : ''}`}
                                 onClick={() => handleToggleFriend(follower)}
                                 style={{ cursor: 'pointer' }}
                              >
                                 <div className="d-flex align-items-center">
                                    <div className="tag-friend-avatar">
                                       <img
                                          src={follower?.follower?.user_setting?.photo || "https://uzone360live.s3.amazonaws.com/noimage.png"}
                                          alt={follower?.follower?.first_name}
                                          className="rounded-circle"
                                          style={{ width: 40, height: 40, objectFit: 'cover' }}
                                       />
                                    </div>
                                    <div className="flex-grow-1 ms-3">
                                       <h6 className="mb-0">
                                          {follower?.follower?.first_name} {follower?.follower?.last_name}
                                       </h6>
                                    </div>
                                    <div>
                                       <Form.Check
                                          type="checkbox"
                                          checked={selected}
                                          onChange={() => {}}
                                       />
                                    </div>
                                 </div>
                              </ListGroup.Item>
                           );
                        })}
                     </ListGroup>
                  )}
               </div>
            )}
         </Modal.Body>
         <Modal.Footer>
            <Button variant="secondary" onClick={handleCancel}>
               Cancel
            </Button>
            <Button variant="primary" onClick={handleDone}>
               Done ({tempSelectedFriends.length})
            </Button>
         </Modal.Footer>
      </Modal>
   );
};

export default TagFriendSelector;
